import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Save, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { api, errMsg, downloadFile } from "@/lib/api";
import { inr, todayISO, addDays, num, VEHICLE_TYPES } from "@/lib/format";
import { PageHeader, Panel } from "@/components/common";
import { FieldGrid } from "@/components/Field";

const TERMS = "1. Toll, parking and state entry tax extra at actuals.\n2. Driver allowance applicable for duties beyond 10 PM.\n3. KM and time calculated garage to garage.\n4. 50% advance for outstation bookings.";

export default function QuotationForm() {
  const { id } = useParams();
  const nav = useNavigate();
  const [q, setQ] = useState({ quote_date: todayISO(), valid_until: addDays(todayISO(), 15), status: "Draft", include_gst: true, gst_rate: 5, terms: TERMS,
    items: [{ description: "Sedan (Dzire/Aura) · 8hr / 80km local package", qty: 1, rate: "" }] });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (id) api.get(`/quotations/${id}`).then((r) => setQ(r.data));
    else api.get("/settings").then((r) => setQ((s) => ({ ...s, gst_rate: r.data.default_gst_rate ?? 5 })));
  }, [id]);

  const change = (k, v) => setQ((s) => ({ ...s, [k]: v }));
  const upItem = (i, k, v) => setQ((s) => ({ ...s, items: s.items.map((x, j) => (j === i ? { ...x, [k]: v } : x)) }));
  const t = useMemo(() => {
    const sub = q.items.reduce((a, i) => a + num(i.qty || 1) * num(i.rate), 0);
    const gst = q.include_gst ? (sub * num(q.gst_rate)) / 100 : 0;
    return { sub, gst, total: sub + gst };
  }, [q]);

  const save = async (andPdf) => {
    setSaving(true);
    try {
      const { data } = id ? await api.put(`/quotations/${id}`, q) : await api.post("/quotations", q);
      toast.success(`Quotation ${data.quote_no} saved`);
      if (andPdf) await downloadFile(`/quotations/${data.id}/pdf`, `${data.quote_no.replace(/\//g, "-")}.pdf`).catch(() => {});
      nav("/quotations");
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  const client = [
    { key: "client_name", label: "Client Name", required: true },
    { key: "client_company", label: "Company" },
    { key: "client_mobile", label: "Mobile", type: "tel" },
    { key: "client_email", label: "Email", type: "email" },
    { key: "client_address", label: "Address", span: 2 },
  ];
  const meta = [
    { key: "quote_date", label: "Quotation Date", type: "date" },
    { key: "valid_until", label: "Valid Until", type: "date" },
    { key: "status", label: "Status", type: "select", options: ["Draft", "Sent", "Accepted", "Rejected"] },
    { key: "vehicle_type", label: "Vehicle Type", type: "select", options: VEHICLE_TYPES },
    { key: "trip_details", label: "Trip Details", span: 2, placeholder: "e.g. Sambhajinagar → Shirdi → Nashik, 2 days" },
  ];

  return (
    <div data-testid="quotation-form-page">
      <Link to="/quotations" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-4"><ArrowLeft className="h-4 w-4 mr-1" /> Quotations</Link>
      <PageHeader eyebrow={id ? q.quote_no : "Document Generator"} title={id ? "Edit Quotation" : "New Quotation"}
        actions={<>
          <Button variant="outline" onClick={() => save(false)} disabled={saving} className="h-11 sm:h-10" data-testid="quotation-save-btn"><Save className="h-4 w-4 mr-1.5" /> Save</Button>
          <Button onClick={() => save(true)} disabled={saving} className="h-11 sm:h-10" data-testid="quotation-save-pdf-btn"><FileDown className="h-4 w-4 mr-1.5" /> Save & PDF</Button>
        </>} />
      <div className="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-6 items-start">
        <div className="space-y-6 min-w-0">
          <Panel title="Client"><FieldGrid fields={client} data={q} onChange={change} testPrefix="quote" /></Panel>
          <Panel title="Quotation & Trip"><FieldGrid fields={meta} data={q} onChange={change} testPrefix="quote" /></Panel>
          <Panel title="Line Items" action={<Button size="sm" variant="outline" onClick={() => change("items", [...q.items, { description: "", qty: 1, rate: "" }])} data-testid="quote-add-item-btn"><Plus className="h-4 w-4 mr-1" /> Add item</Button>}>
            <div className="space-y-3">{q.items.map((it, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`quote-item-${i}`}>
                <Input className="col-span-12 sm:col-span-6" placeholder="Description" value={it.description} onChange={(e) => upItem(i, "description", e.target.value)} data-testid={`quote-item-desc-${i}`} />
                <Input className="col-span-3 sm:col-span-1 num" type="number" placeholder="Qty" value={it.qty} onChange={(e) => upItem(i, "qty", e.target.value)} data-testid={`quote-item-qty-${i}`} />
                <Input className="col-span-4 sm:col-span-2 num" type="number" placeholder="Rate" value={it.rate} onChange={(e) => upItem(i, "rate", e.target.value)} data-testid={`quote-item-rate-${i}`} />
                <div className="col-span-3 sm:col-span-2 num text-sm text-right font-semibold">{inr(num(it.qty || 1) * num(it.rate))}</div>
                <Button variant="ghost" size="icon" className="col-span-2 sm:col-span-1 text-rose-600" onClick={() => change("items", q.items.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}</div>
          </Panel>
          <Panel title="Terms & Conditions"><FieldGrid fields={[{ key: "terms", label: "Terms", type: "textarea", rows: 5, span: 3 }]} data={q} onChange={change} testPrefix="quote" /></Panel>
        </div>
        <aside className="xl:sticky xl:top-24 rounded-xl border bg-card p-5 shadow-sm" data-testid="quote-summary">
          <div className="eyebrow mb-3">Totals</div>
          <div className="flex items-center justify-between mb-3">
            <Label htmlFor="gst-sw" className="text-sm">Include GST</Label>
            <Switch id="gst-sw" checked={!!q.include_gst} onCheckedChange={(v) => change("include_gst", v)} data-testid="quote-gst-switch" />
          </div>
          {q.include_gst && (
            <div className="flex items-center justify-between mb-3 gap-3">
              <span className="text-sm text-muted-foreground">GST %</span>
              <Input type="number" className="w-24 num h-9" value={q.gst_rate} onChange={(e) => change("gst_rate", e.target.value)} data-testid="quote-gst-rate" />
            </div>
          )}
          <div className="flex justify-between py-1.5 text-sm"><span className="text-muted-foreground">Sub total</span><span className="num">{inr(t.sub, 2)}</span></div>
          <div className="flex justify-between py-1.5 text-sm"><span className="text-muted-foreground">GST</span><span className="num">{inr(t.gst, 2)}</span></div>
          <div className="border-t mt-2 pt-3"><div className="eyebrow">Total</div><div className="font-heading text-3xl font-bold num mt-1" data-testid="quote-total">{inr(t.total, 2)}</div></div>
        </aside>
      </div>
    </div>
  );
}
